import React, { useState, useMemo } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, Check } from "lucide-react";

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const CodeBlock: React.FC<{
  className?: string;
  children?: React.ReactNode;
  inline?: boolean;
}> = ({ className, children, inline, ...props }) => {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || "");
  const language = match ? match[1] : "";
  const codeString = String(children).replace(/\n$/, "");

  if (inline) {
    return (
      <code
        className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 text-xs font-mono border border-slate-200"
        {...props}
      >
        {children}
      </code>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(codeString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group my-2 rounded-lg overflow-hidden border border-slate-200">
      <div className="flex items-center justify-between bg-slate-100 px-3 py-1.5 border-b border-slate-200">
        <span className="text-xs font-medium text-slate-500 uppercase">
          {language || "code"}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-3 h-3 text-green-600" />
              <span className="text-green-600">Copied</span>
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className="overflow-x-auto p-3 bg-slate-50 text-sm">
        <code className={`font-mono text-slate-800 ${className || ""}`} {...props}>
          {children}
        </code>
      </pre>
    </div>
  );
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = "",
}) => {
  const components = useMemo(() => ({
    code({ className, children, ...props }: any) {
      const isInline = !className && typeof children === "string" && !children.includes("\n");
      return (
        <CodeBlock className={className} inline={isInline} {...props}>
          {children}
        </CodeBlock>
      );
    },
    table({ children }: any) {
      return (
        <div className="overflow-x-auto my-2 rounded-lg border border-slate-200">
          <table className="min-w-full text-sm">{children}</table>
        </div>
      );
    },
    thead({ children }: any) {
      return <thead className="bg-slate-50 border-b border-slate-200">{children}</thead>;
    },
    th({ children }: any) {
      return (
        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
          {children}
        </th>
      );
    },
    td({ children }: any) {
      return <td className="px-3 py-2 text-slate-700 border-t border-slate-100">{children}</td>;
    },
    a({ href, children }: any) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline underline-offset-2"
        >
          {children}
        </a>
      );
    },
    blockquote({ children }: any) {
      return (
        <blockquote className="border-l-3 border-blue-400 bg-blue-50/50 pl-3 py-1 my-2 text-slate-700 italic">
          {children}
        </blockquote>
      );
    },
    ul({ children }: any) {
      return <ul className="list-disc pl-5 space-y-0.5 my-1">{children}</ul>;
    },
    ol({ children }: any) {
      return <ol className="list-decimal pl-5 space-y-0.5 my-1">{children}</ol>;
    },
    li({ children }: any) {
      return <li className="text-slate-700 leading-relaxed">{children}</li>;
    },
    h1({ children }: any) {
      return <h1 className="text-lg font-bold text-slate-900 mt-3 mb-1">{children}</h1>;
    },
    h2({ children }: any) {
      return <h2 className="text-base font-bold text-slate-900 mt-2.5 mb-1">{children}</h2>;
    },
    h3({ children }: any) {
      return <h3 className="text-sm font-bold text-slate-900 mt-2 mb-0.5">{children}</h3>;
    },
    p({ children }: any) {
      return <p className="text-sm text-slate-700 leading-relaxed my-1">{children}</p>;
    },
    hr() {
      return <hr className="border-slate-200 my-3" />;
    },
  }), []);

  return (
    <div className={`prose prose-sm prose-slate max-w-none ${className}`}>
      <Markdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </Markdown>
    </div>
  );
};

export default React.memo(MarkdownRenderer);
